import { createClient } from '@supabase/supabase-js';
import { systemLogger } from '@/lib/logging';

// Ensure your .env file has these variables
const supabaseUrl = import.meta.env['VITE_PUBLIC_SUPABASE_URL'];
const supabaseAnonKey = import.meta.env['VITE_PUBLIC_SUPABASE_ANON_KEY'];

if (!supabaseUrl || !supabaseAnonKey) {
  systemLogger.error('❌ Supabase Configuration Error:', {
    url: supabaseUrl,
    keyExists: !!supabaseAnonKey,
    allEnvVars: Object.keys(import.meta.env).filter(key => key.includes('SUPABASE'))
  });
  throw new Error('Supabase URL or Anonymous Key is missing. Check your .env file and Vite configuration.');
}

// ---------------------------------------------------------------------------
// In-process lock: replaces navigator.locks to prevent CROSS-TAB lock contention.
//
// supabase-js v2.49+ uses navigator.locks (exclusive mode) to serialize ALL
// getSession() calls. The lock name is shared across ALL tabs on the same origin.
// When Tab A holds the lock (e.g. during _recoverAndRefresh, token refresh, or
// signOut — all of which make network calls), Tab B's getSession() calls queue
// behind it with INFINITE timeout. supabase.functions.invoke() internally calls
// getSession() via _getAccessToken(), so the entire app in Tab B hangs.
//
// This in-process lock serializes operations WITHIN a single tab (preventing
// races) but does NOT block other tabs. Cross-tab session sync is still handled
// by BroadcastChannel (which supabase-js uses independently of locks).
// ---------------------------------------------------------------------------
const inProcessLocks: Record<string, Promise<unknown>> = {};

function tabLocalLock<R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>
): Promise<R> {
  const previous = inProcessLocks[name] ?? Promise.resolve();

  let resolve: (_value: R | PromiseLike<R>) => void;
  let reject: (_reason: unknown) => void;
  const resultPromise = new Promise<R>((res, rej) => {
    resolve = res;
    reject = rej;
  });

  // If acquireTimeout === 0, fail immediately if lock is busy
  if (acquireTimeout === 0 && inProcessLocks[name]) {
    // Check if previous is still pending
    let isPending = true;
    previous.then(
      () => { isPending = false; },
      () => { isPending = false; }
    );
    // Use a microtask to check — if still pending after this tick, it's busy
    const check = Promise.resolve().then(() => {
      if (isPending) {
        reject(new Error(`Acquiring lock "${name}" immediately failed`));
        return;
      }
      // Lock is free, proceed
      previous.catch(() => {}).then(() => fn()).then(resolve!, reject!);
    });
    inProcessLocks[name] = check.catch(() => {});
    return resultPromise;
  }

  const operation = previous
    .catch(() => {}) // ignore errors from previous holder
    .then(() => fn());

  operation.then(resolve!, reject!);

  // Store the operation so the next caller waits for it
  inProcessLocks[name] = operation.catch(() => {});

  return resultPromise;
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    // CRITICAL: Use tab-local lock instead of navigator.locks to prevent
    // cross-tab lock contention that causes the app to hang.
    lock: tabLocalLock,
    storage: {
      getItem: (key) => {
        try {
          return localStorage.getItem(key);
        } catch (_error) {
          systemLogger.error('Failed operation:', _error);
          return null;
        }
      },
      setItem: (key, value) => {
        try {
          localStorage.setItem(key, value);
        } catch (_error) {
          systemLogger.error('Failed operation:', _error);
        }
      },
      removeItem: (key) => {
        try {
          localStorage.removeItem(key);
        } catch (_error) {
          systemLogger.error('Failed operation:', _error);
        }
      }
    },
    storageKey: `sb-${supabaseUrl.split('//')[1]?.split('.')[0]}-auth-token`,
    debug: false,
  },
  global: {
    headers: {
      'X-Client-Info': 'flexireo-webapp'
    }
  }
}); 